import {useMutation} from '@tanstack/react-query';
import {APIError} from 'data/errors/api-error';
import {HttpStatusCode} from 'data/protocols/http/http-client';
import {MUTATION_KEYS} from 'infra/libs/react-query/mutationKeys';
import {FeaturesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {updateContactDataCache} from 'presentation/queries/useContactDataQuery/utils/updateContactDataCache';
import {patchUpdatePhone} from '../../services/patchUpdatePhone';
import {useChangeDataAlertStore} from '../../store/changeDataAlertStore';
import {MessageEnum} from '../../store/changeDataAlertStore/types';
import {useChangeDataFormStateStore} from '../../store/changeDataFormStateStore';
import {AccordionStateEnum} from '../../store/formAccordionStore/types';
import {useGetProgressQuery} from '../useGetProgressQuery';
import {ChangePhoneMutationProps} from './types';
import {useRegistrationUpdateStatusStore} from 'presentation/pages/private/store/useRegistrationUpdateStatusStore';
import {queryClient} from 'infra/libs/react-query';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';

export function useChangePhoneMutation({
	handleClose,
	handleSuccess,
	setError,
	type,
	reset,
}: ChangePhoneMutationProps) {
	const setChangeDataFormState = useChangeDataFormStateStore(state => state.updateFormState);
	const setChangeDataAlertState = useChangeDataAlertStore(state => state.setStatus);
	const {setNeedsUpdate} = useRegistrationUpdateStatusStore();
	const {refetch} = useGetProgressQuery();

	const {isEnabled: isNewProfileFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_FEATURE_NEW_PROFILE,
	);

	return useMutation({
		mutationKey: [MUTATION_KEYS.CHANGE_PHONE],
		mutationFn: patchUpdatePhone,
		onSuccess: data => {
			updateContactDataCache({
				telefone: data.celular,
			});

			setChangeDataFormState({
				name: AccordionStateEnum.CELLPHONE,
				isFormDirty: false,
				isFormActive: false,
			});

			if (isNewProfileFeatureFlagEnabled) {
				refetch();
			}

			setChangeDataAlertState({
				type,
				typeMessage: MessageEnum.SUCCESS,
				status: true,
			});
			queryClient.setQueryData([QUERY_KEYS.REGISTRATION_UPDATE_STATUS], {
				necessitaAtualizacao: false,
			});
			setNeedsUpdate(false);
			handleSuccess();
		},
		onError: err => {
			if (err instanceof APIError && err.statusCode === HttpStatusCode.unprocessable) {
				setError(err.message.toLowerCase().includes('expir') ? 'expired' : 'invalid');
				return;
			}

			if (!isNewProfileFeatureFlagEnabled) {
				setChangeDataFormState({
					name: AccordionStateEnum.CELLPHONE,
					isFormDirty: false,
					isFormActive: false,
				});
			}

			setChangeDataAlertState({
				type,
				typeMessage: MessageEnum.ERROR,
				status: true,
			});
			handleClose();
		},
		onSettled: () => {
			reset();
		},
	});
}
